import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Lightbulb,
  Sparkles,
  Send,
  X,
  RefreshCw,
  CheckCircle2,
  ArrowUpRight,
  MessageSquare,
  Bell,
} from 'lucide-react';
import {
  shareThoughtWithAmalie,
  executeManagerAction,
  sendTestSlackAlert,
  SharedThoughtResult,
} from '../../services/dailyOverviewApi';

const MANAGER_LABELS: Record<string, { name: string; role: string; badge: string }> = {
  amalie: {
    name: 'Amalie Ryberg',
    role: 'Chief of Staff · Strategy & Follow-Up',
    badge: 'bg-slate-800 text-white border-slate-700',
  },
  eskild: {
    name: 'Eskild',
    role: 'Head of SEO · Content & Rankings',
    badge: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  },
  bruun: {
    name: 'Bruun',
    role: 'Head of Email · SendEngine & Offers',
    badge: 'bg-purple-50 text-purple-800 border-purple-200',
  },
  fox: {
    name: 'Fox',
    role: 'Head of Paid Ads · Budget & Search',
    badge: 'bg-blue-50 text-blue-800 border-blue-200',
  },
  frederik: {
    name: 'Frederik Hartmann',
    role: 'Deliverability & Engagement Lead',
    badge: 'bg-amber-50 text-amber-900 border-amber-200',
  },
};

export default function ShareThoughtModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [thought, setThought] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SharedThoughtResult | null>(null);
  const [executingKey, setExecutingKey] = useState<string | null>(null);
  const [executedMessage, setExecutedMessage] = useState<string | null>(null);

  // Quick Slack Alert configuration & test inside modal
  const [showSlackConfig, setShowSlackConfig] = useState(false);
  const [slackWebhookInput, setSlackWebhookInput] = useState('');
  const [testingSlack, setTestingSlack] = useState(false);
  const [slackStatusMsg, setSlackStatusMsg] = useState<string | null>(null);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const clean = thought.trim();
    if (!clean || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    setExecutedMessage(null);
    try {
      const res = await shareThoughtWithAmalie(clean, false);
      if (res.result) {
        setResult(res.result);
      }
      setThought('');
      window.dispatchEvent(new CustomEvent('afima:thought-shared', { detail: res }));
    } catch (err: any) {
      setError(err?.message || 'Could not queue thought');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickExecute = async (actionType: string, targetId: string, reflectionId?: number) => {
    const key = `${actionType}:${targetId}`;
    setExecutingKey(key);
    try {
      const res = await executeManagerAction(actionType, targetId, reflectionId);
      setExecutedMessage(res.executionResult?.message || 'Action executed');
      window.dispatchEvent(new CustomEvent('afima:thought-shared', { detail: res }));
    } catch (err: any) {
      setError(err?.message || 'Action execution failed');
    } finally {
      setExecutingKey(null);
    }
  };

  const handleTestSlack = async (e: React.FormEvent) => {
    e.preventDefault();
    setTestingSlack(true);
    setSlackStatusMsg(null);
    setError(null);
    try {
      const res = await sendTestSlackAlert(slackWebhookInput.trim() || undefined, 'amalie');
      if (res.slackResult?.sentSlack) {
        setSlackStatusMsg('✓ Connected! Amalie & all 5 managers sent a test alert to your Slack channel.');
      } else {
        setSlackStatusMsg(
          res.slackResult?.error ||
            'Paste your Slack Incoming Webhook URL (https://hooks.slack.com/services/...) above to activate live Slack alerts.'
        );
      }
      window.dispatchEvent(new CustomEvent('afima:thought-shared', { detail: res }));
    } catch (err: any) {
      setError(err?.message || 'Slack test failed');
    } finally {
      setTestingSlack(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white text-xs font-semibold shadow-sm transition-all cursor-pointer"
        title="Share a thought or idea — Amalie Ryberg will triage it, assign the right manager, and follow up on it"
      >
        <Lightbulb className="w-3.5 h-3.5 text-amber-300" />
        <span>Share Thought</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl overflow-hidden my-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 px-6 py-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-400/20 border border-amber-300/40 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-amber-300" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-bold tracking-tight">
                      Share a Thought with Amalie Ryberg
                    </h3>
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                      Chief of Staff Triage
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 mt-0.5">
                    Drop any raw idea, question, or hunch. Amalie will evaluate it, assign it to the right department manager, and track follow-through.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowSlackConfig((v) => !v)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-white/10 hover:bg-white/20 text-slate-200 transition-colors cursor-pointer"
                  title="Configure Slack Webhook for time-sensitive alerts from all 5 agents"
                >
                  <Bell className="w-3.5 h-3.5 text-amber-300" />
                  <span>Slack Alerts</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Optional Collapsible Slack Pager Config */}
            {showSlackConfig && (
              <form
                onSubmit={handleTestSlack}
                className="bg-slate-50 border-b border-slate-200 px-6 py-3.5 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                    <Bell className="w-3.5 h-3.5 text-indigo-600" />
                    All-Agent Time-Sensitive Slack Pager (Fox · Eskild · Bruun · Frederik · Amalie)
                  </span>
                  <span className="text-[11px] text-slate-500">
                    Triggers on urgent 2h reflections (`needs_founder`) &amp; War Room blockers
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <input
                    type="url"
                    value={slackWebhookInput}
                    onChange={(e) => setSlackWebhookInput(e.target.value)}
                    placeholder="Paste Slack Incoming Webhook URL (https://hooks.slack.com/services/...) or leave blank if already saved"
                    className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-slate-300 bg-white text-slate-900"
                  />
                  <button
                    type="submit"
                    disabled={testingSlack}
                    className="px-3.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold shrink-0 cursor-pointer"
                  >
                    {testingSlack ? 'Sending Ping…' : 'Save & Send Test Slack Alert'}
                  </button>
                </div>
                {slackStatusMsg && (
                  <div className="text-xs font-medium text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-1.5">
                    {slackStatusMsg}
                  </div>
                )}
              </form>
            )}

            {/* Body */}
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    What&apos;s on your mind?
                  </label>
                  <textarea
                    rows={3}
                    value={thought}
                    onChange={(e) => setThought(e.target.value)}
                    placeholder="e.g. 'Should we push more non-finance campaigns in Sweden if they get higher open rates, or does that hurt EPC?' or 'Check if Sambla conversions dropped in NO this week and whether Eskild or Bruun should adjust...'"
                    className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-600 focus:outline-none"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Quick thought starters */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] text-slate-400 font-medium mr-1">Examples:</span>
                  {[
                    'Check why Non-Finance emails have 43.5% open rate vs 38.2% for Finance — should we adjust our sendout mix?',
                    'Are we capitalizing on the competitor partner changes in Sweden across both SEO and Email?',
                    'Which partner has the highest EPC over the last 14 days and are we giving them enough volume?',
                  ].map((sample, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setThought(sample)}
                      className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 hover:bg-indigo-50 text-slate-600 hover:text-indigo-700 border border-slate-200 transition-colors text-left truncate max-w-xs cursor-pointer"
                    >
                      {sample.slice(0, 56)}…
                    </button>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-1">
                  <span className="text-[11px] text-slate-500">
                    Instantly queues your note to the Business Compass &amp; manager queue — no waiting for an AI reply.
                  </span>
                  <button
                    type="submit"
                    disabled={isSubmitting || !thought.trim()}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold shadow-xs transition-colors shrink-0 cursor-pointer"
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        <span>Queuing…</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        <span>Queue Note</span>
                      </>
                    )}
                  </button>
                </div>
              </form>

              {error && (
                <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-xs text-rose-700">
                  {error}
                </div>
              )}

              {executedMessage && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-xs text-emerald-800 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>{executedMessage}</span>
                </div>
              )}

              {/* Instant Queued Confirmation */}
              {result && !result.amalieReflection && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-xs text-emerald-900 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>
                      <strong>Queued:</strong> &ldquo;{result.thought}&rdquo; — added to Business Compass &amp; queued for{' '}
                      <strong>@{MANAGER_LABELS[result.assignedTo || '']?.name || result.assignedTo || 'Amalie'}</strong>.
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="px-2.5 py-1 rounded-lg bg-emerald-700 text-white text-[11px] font-semibold hover:bg-emerald-800 shrink-0 cursor-pointer"
                  >
                    Done
                  </button>
                </div>
              )}

              {/* Live Result Display */}
              {result && result.amalieReflection && (
                <div className="space-y-3 pt-2 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Live Chief-of-Staff Triage &amp; Assignment
                    </span>
                    <Link
                      to="/overview"
                      onClick={() => setIsOpen(false)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                    >
                      <span>Open Full AI Managers Overview</span>
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>

                  {/* Step 1: Amalie's Assessment */}
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-2.5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-900 text-white">
                          Amalie Ryberg · Chief of Staff
                        </span>
                        {result.assignedTo && MANAGER_LABELS[result.assignedTo] && (
                          <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2.5 py-0.5 rounded-full">
                            Assigned → @{MANAGER_LABELS[result.assignedTo].name}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-slate-400">
                        Tracked in Business Compass
                      </span>
                    </div>

                    <div className="text-xs text-slate-500 italic bg-white px-3 py-1.5 rounded-lg border border-slate-200/80">
                      Your thought: &ldquo;{result.thought}&rdquo;
                    </div>

                    <p className="text-xs text-slate-800 leading-relaxed">
                      {result.amalieReflection.thoughtProcess}
                    </p>

                    {result.amalieReflection.mentionQuestion && (
                      <div className="rounded-lg bg-indigo-50/90 border border-indigo-200 px-3 py-2 text-xs text-indigo-950">
                        <span className="font-bold">
                          Delegation Brief to @{MANAGER_LABELS[result.assignedTo || '']?.name || result.assignedTo}:{' '}
                        </span>
                        {result.amalieReflection.mentionQuestion}
                      </div>
                    )}

                    {result.amalieReflection.hypothesis && (
                      <div className="text-xs text-slate-700 bg-white px-3 py-2 rounded-lg border border-slate-200">
                        <span className="font-bold text-slate-900">Amalie&apos;s Follow-Up Commitment: </span>
                        {result.amalieReflection.hypothesis}
                      </div>
                    )}
                  </div>

                  {/* Step 2: Assigned Manager's Immediate Response */}
                  {result.assigneeReflection && (
                    <div className="rounded-xl border border-indigo-200 bg-indigo-50/30 p-4 space-y-2.5">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-indigo-600" />
                          <span className="text-xs font-bold text-slate-900">
                            Immediate Response from{' '}
                            {MANAGER_LABELS[result.assigneeReflection.speakerId]?.name ||
                              result.assigneeReflection.speakerName}
                          </span>
                          <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                            Assigned Owner
                          </span>
                        </div>
                        <span className="text-[11px] font-medium text-slate-500">
                          GEO: {result.assigneeReflection.geo}
                        </span>
                      </div>

                      <p className="text-xs text-slate-800 leading-relaxed">
                        {result.assigneeReflection.thoughtProcess}
                      </p>

                      {result.assigneeReflection.hypothesis && (
                        <div className="rounded-lg bg-white border border-indigo-100 px-3 py-2 text-xs text-slate-800">
                          <span className="font-bold text-indigo-900">Action Plan / Hypothesis: </span>
                          {result.assigneeReflection.hypothesis}
                        </div>
                      )}

                      {result.assigneeReflection.executableAction && (
                        <div className="pt-1 flex items-center justify-end">
                          <button
                            type="button"
                            disabled={
                              executingKey ===
                              `${result.assigneeReflection.executableAction.action_type}:${result.assigneeReflection.executableAction.target_id}`
                            }
                            onClick={() =>
                              handleQuickExecute(
                                result.assigneeReflection!.executableAction!.action_type,
                                result.assigneeReflection!.executableAction!.target_id,
                                result.assigneeReflection!.id
                              )
                            }
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-xs transition-colors cursor-pointer"
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                            <span>{result.assigneeReflection.executableAction.button_label}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
